<template>
  <div>
    <div class="closing-date-container">
      <div class="row">
        <label
          id="edit-closing-date-tender-status-label"
          class="tender-status-label"
        >{{ $t('tender.status') }}:</label>
        <label
          id="edit-closing-date-tender-status-value"
          class="status-severity"
          :style="updateLabelColor(status)"
        >{{ status }}</label>
      </div>

      <label
        id="edit-closing-date-label"
        for="closingDate-select"
        class="closing-date-label"
      >{{ $t('tender.closingDate') }}:
        <span class="required-flag" />
        <span style="font-style: italic;"> ({{ $t('hint.closingDateHint', [suggestedDaysITQ, suggestedDaysTender]) }})</span>
      </label>
      <div
        class="row"
        id="edit-closing-date-picker"
      >
        <DatePicker
          format="DD MMM YY"
          :class="{ 'error-highlight': invalidClosingDate }"
          :disabled="disabledDates"
          :calendarButton="true"
          calendarButtonIcon="fa fa-calendar"
          v-model="selectedClosingDate"
          @selected="val => updateClosingDate(val, selectedClosingTime)"
        />
        <HKLSelect
          :class="{ 'closing-time': !invalidClosingDate, 'closing-time error-highlight': invalidClosingDate }"
          :value="selectedClosingTime || undefined"
          :options="closingTimeHklOptions"
          :showSearch="true"
          placeholder=""
          @change="val => updateClosingDate(selectedClosingDate, val || '')"
        />
      </div>

      <div class="onsite-visit">
        <div class="tender-label">
          <input
            :disabled="onsiteVisitRequired && !onsiteContact.changedFlag"
            id="edit-closing-date-onsite-visit-required"
            type="checkbox"
            v-model="onsiteVisitRequired"
            @change="event => updateOnsiteInfo(event.target.checked)"
          >
          <label id="edit-closing-date-onsite-visit-required-label">{{ $t('tender.general.onsiteVisitRequired') }}</label>
        </div>

        <div v-if="onsiteVisitRequired">
          <label
            id="edit-closing-date-onsite-location-label"
            class="tender-label onsite-location__label"
          >{{ $t('tender.general.onSiteLocation') }}</label>
          <input
            type="text"
            id="edit-closing-date-onsite-location-value"
            class="onsite-location"
            :class="{'error-highlight': !onsiteLocation && invalidContactInfo}"
            :placeholder="$t('tender.general.onSiteLocation')"
            v-model="onsiteLocation"
            v-fusioninputcheck="{type: 'string', tip: $t('hint.inputTip.location'),}"
            required
            @change="event => updateOnsiteLocation(event.target.value)"
          >
          <label
            id="edit-closing-date-onsite-contact-label"
            class="tender-label onsite-contact__label"
          >{{ $t('tender.general.onSiteContact') }}</label>
          <input
            type="text"
            id="edit-closing-date-onsite-contact-name"
            :placeholder="$t('user.name')"
            class="onsite-contact"
            :class="{'error-highlight': !onsiteContact.name && invalidContactInfo}"
            :value="onsiteContact.name"
            v-fusioninputcheck="{type: 'string', tip: $t('hint.inputTip.name'),}"
            required
            @change="event => updateContact(['general', 'onsiteContact', 'name'], event.target.value)"
          >
          <input
            type="text"
            id="edit-closing-date-onsite-contact-email"
            :placeholder="$t('user.email')"
            class="onsite-contact"
            :class="{'error-highlight': !onsiteContact.email && invalidContactInfo}"
            :value="onsiteContact.email"
            v-fusioninputcheck="{type: 'string', tip: $t('hint.inputTip.email'), pattern: email}"
            required
            @change="event => updateContact(['general', 'onsiteContact', 'email'], event.target.value)"
          >
          <input
            type="text"
            id="edit-closing-date-onsite-contact-phone"
            :placeholder="$t('user.phone')"
            class="onsite-contact"
            :class="{'error-highlight': !onsiteContact.phone && invalidContactInfo}"
            :value="onsiteContact.phone"
            v-fusioninputcheck="{type: 'string', tip: $t('hint.inputTip.phone'), pattern: phone}"
            required
            @change="event => updateContact(['general', 'onsiteContact', 'phone'], event.target.value)"
          >
        </div>
      </div>

      <div id="edit-closing-date-vendors">
        <table>
          <thead>
            <tr>
              <th>{{ $t('vendor.invitedVendors') }}</th>
              <th>{{ $t('vendor.onSiteVisitDateTime') }}</th>
              <th>{{ $t('vendor.status') }}</th>
              <th>{{ $t('handover.lastUpdateOn') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(bid, index) in bids"
              :key="index"
            >
              <td class="vendor-name">{{ deep(bid, 'company.name') || '' }}</td>
              <td
                class="visit-date row"
                :class="{ 'error-highlight': isAfterClosingDate[index] || (highlightflags[index] && invalidVisitDayAndTime) }"
                :id="'edit-closing-date-vendors-' + index"
              >
                <div class="clear-date">
                  <span
                    @click="updateVisitDate(index)"
                    v-if="showClearIcon(onsiteVisitTimes[index])"
                  >
                    <i>
                      <span
                        class="fa fa-times fa-lg"
                        aria-hidden="true"
                      />
                    </i>
                  </span>
                </div>
                <DatePicker
                  :disabledPicker="!onsiteVisitRequired"
                  format="DD MMM YY"
                  :disabled="onSiteVisiteDisabledDates()"
                  :clearButton="false"
                  :calendarButton="true"
                  calendarButtonIcon="fa fa-calendar"
                  :value="onsiteVisitTimes[index].visitDay"
                  @selected="val => updateVisitDate(index, val, onsiteVisitTimes[index].visitTime)"
                />
                <HKLSelect
                  :disabled="!onsiteVisitRequired"
                  class="visit-time"
                  :value="onsiteVisitTimes[index].visitTime || undefined"
                  :options="visitTimeHklOptions"
                  :showSearch="true"
                  placeholder=""
                  @change="val => updateVisitDate(index, onsiteVisitTimes[index].visitDay, val || '')"
                />
              </td>
              <td
                class="vendor-status"
                :id="'edit-closing-date-vendors-status-' + index"
              >
                {{ bidStatus(bid) }}
              </td>
              <td class="vendor-respond-date">
                <span v-if="bid.respondedOn || bid.startBidAt">{{ formatLastSubmittedOn(bid) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div
          class="status-summary"
          v-if="isTenderInAddendum"
        >
          <label>SUBMITTED:</label>
          <span>{{ totalSubmittedbids(bids) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import DatePicker from '@/controls/DatePicker'
import { dateFormat, email, phone } from '@/utils/regex'
import dateUtil from '@/utils/dateUtil'
import cookie from '@/utils/cookie'
import { tenderStatus, vendorBidStatus, bidStatusMapHealthCheck } from '@/utils/locale'
import { closingTimeOptions } from '@/utils/consts'
import { mapMutations, mapGetters } from 'vuex'
import mutationTypes from '@/store/mutation-types'
import { parseStatusText, isTenderInAddendum } from '@/api/tenders/request'
import { getRevisionByVersionNum } from '@/api/tender-addendums/request'

export default {
  name: 'closingDate',
  components: {
    HKLSelect,
    DatePicker,
  },
  props: {
    invalidClosingDate: Boolean,
    invalidContactInfo: Boolean,
    invalidVisitDayAndTime: Boolean,
    highlightflags: {
      type: Array,
      default () {
        return []
      },
    },
  },

  data () {
    const visitTimes = [
      '',
      '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30', '04:00', '04:30', '05:00', '05:30',
      '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
      '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
      '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00', '23:30',
    ]
    const suggestedDaysITQ = cookie.get('suggestedDaysITQ') || 5
    const suggestedDaysTender = cookie.get('suggestedDaysTender') || 14
    const toDate = new Date(Date.now())
    toDate.setDate(toDate.getDate() - 1)
    return {
      toDate,
      email,
      phone,
      closingTimeOptions,
      visitTimes,
      suggestedDaysITQ,
      suggestedDaysTender,
      isAfterClosingDate: [],
      visitTimesUpdated: [],
      selectedClosingDate: null,
      selectedClosingTime: null,
      onsiteVisitTimes: {},
      originalVisitTimes: {},
      originVisitTimeSet: false,
      deep,
      lastVersionClosingDate: {
        type: Date,
        default: null,
      },
    }
  },

  methods: {
    ...mapMutations('tenders', {
      updateTenderPathField: mutationTypes.TENDER_UPDATE_PATH_FIELD,
    }),

    updateLabelColor (status) {
      switch (status) {
        case tenderStatus.DRAFT:
        case tenderStatus.DECLINED:
          return 'color: #d50000;'
        default:
          return 'color: #009588'
      }
    },

    bidStatus (bid) {
      let status = null
      status = bidStatusMapHealthCheck[bid.status]
      return status
    },
    totalSubmittedbids (bids) {
      let counter = 0
      bids.forEach((bid) => {
        if (bid.status === vendorBidStatus.SUBMITTED) {
          counter++
        }
      })
      return counter
    },

    formatLastSubmittedOn (bid) {
      if (bid.respondedOn || bid.startBidAt) {
        const updatedOn = bid.respondedOn || bid.startBidAt
        const date = dateUtil.formatDate(updatedOn, true, true)
        return date
      }
      return ''
    },
    updateOnsiteLocation (newValue) {
      this.updateTenderPathField({
        path: ['general', 'onsiteLocation'],
        newValue,
      })
    },
    updateTenderClosingDate (newValue) {
      this.updateTenderPathField({
        path: ['general', 'closingDate'],
        newValue,
      })
    },

    updateClosingDate (selectedDay, selectedTime) {
      this.selectedClosingDate = selectedDay
      this.selectedClosingTime = selectedTime
      const emptyTime = !selectedTime || selectedTime === '--/--'
      if (!selectedDay || emptyTime) {
        this.updateTenderClosingDate(null)
        this.$emit('updated:partial', (selectedDay && emptyTime) || (!selectedDay && !emptyTime))
        return
      }
      this.$emit('updated:partial', false)
      const closingDate = dateUtil.toMoment(new Date(dateUtil.parse(selectedDay)))
      dateUtil.addClosingTime(closingDate, selectedTime)
      this.updateTenderClosingDate(closingDate.valueOf())
      this.validateExistingOnSiteVisitTimes(closingDate.valueOf())
    },

    updateVendorVisitDate (index, newValue) {
      this.updateTenderPathField({
        path: ['bids', index, 'visitDate'],
        newValue,
      })
    },
    showClearIcon (onsiteVisitTime = {}) {
      return onsiteVisitTime && (onsiteVisitTime.visitDay || onsiteVisitTime.visitTime)
    },
    setVisitDateTemporarily (index, visitDay, visitTime) {
      this.updateTenderPathField({
        path: ['bids', index, 'visitDay'],
        newValue: visitDay,
      })
      this.updateTenderPathField({
        path: ['bids', index, 'visitTime'],
        newValue: visitTime,
      })
    },

    formatVisitDate (visitDay, visitTime) {
      const visitDate = dateUtil.toMoment(new Date(dateUtil.parse(visitDay)))
      if (visitTime) {
        const timeArray = visitTime.split(':')
        if (timeArray.length === 2) {
          visitDate.add(timeArray[0].trim(), 'hours')
          visitDate.add(timeArray[1].trim(), 'minutes')
        }
      }

      return visitDate.valueOf()
    },

    updateVisitDate (index, visitDay, visitTime) {
      this.onsiteVisitTimes[index].visitTime = visitTime
      this.onsiteVisitTimes[index].visitDay = visitDay
      if (!visitDay || !visitTime) {
        this.updateVendorVisitDate(index, null)
        this.setVisitDateTemporarily(index, visitDay, visitTime)
        this.isAfterClosingDate[index] = (visitDay && !visitTime) || (!visitDay && visitTime) || false
        this.$emit('update:invalidVisitTimes', this.isAfterClosingDate)
        return
      }

      const visitDate = this.formatVisitDate(visitDay, visitTime)
      if (visitDate !== this.originalVisitTimes[this.bids[index].id]) {
        this.updateVisitTimeValueAndValidation(index, visitDate)
      }
    },

    updateVisitTimeValueAndValidation (index, visitDate) {
      this.updateVendorVisitDate(index, visitDate)
      this.setVisitDateTemporarily(index, null, null)
      const closingDateTime = this.closingDate ? dateUtil.toMoment(this.closingDate).valueOf() : 0
      this.visitTimesUpdated[index] = true
      this.checkIfVisitTimeValid(index, closingDateTime, visitDate)
      this.$emit('update:invalidVisitTimes', this.isAfterClosingDate)
    },

    checkIfVisitTimeValid (index, closingDateTime = 0, visitDateTime = 0) {
      if (+visitDateTime >= +closingDateTime || (this.visitTimesUpdated[index] && +visitDateTime <= Date.now())) {
        this.isAfterClosingDate[index] = true
      } else {
        this.isAfterClosingDate[index] = false
      }
    },

    validateExistingOnSiteVisitTimes (closingDateTime) {
      if (!this.onsiteVisitRequired) return
      for (let i = 0; i < this.bids.length; i += 1) {
        const bid = this.bids[i]
        if (bid.visitDate && this.originalVisitTimes[bid.id] !== bid.visitDate) {
          const visitDateTime = dateUtil.toMoment(bid.visitDate).valueOf()
          this.checkIfVisitTimeValid(i, closingDateTime, visitDateTime)
        }
      }
      this.$emit('update:invalidVisitTimes', this.isAfterClosingDate)
    },

    updateContact (path, newValue) {
      this.updateTenderPathField({
        path,
        newValue,
      })
    },

    resetOnsiteVisitDates () {
      for (let i = 0; i < this.bids.length; i += 1) {
        this.updateVendorVisitDate(i, null)
        this.onsiteVisitTimes[i] = {}
        this.isAfterClosingDate[i] = false
      }
    },

    cleanOnSiteInfo () {
      const onsiteContact = {
        name: '',
        email: '',
        phone: '',
      }
      this.updateContact(['general', 'onsiteContact'], onsiteContact)
      this.updateOnsiteLocation('')
      this.resetOnsiteVisitDates()
    },

    updateOnsiteInfo (checked) {
      this.updateTenderPathField({
        path: ['general', 'onsiteVisitRequired'],
        newValue: checked,
      })
      if (!checked) {
        this.cleanOnSiteInfo()
      } else {
        this.updateTenderPathField({
          path: ['general', 'onsiteContact', 'changedFlag'],
          newValue: true,
        })
      }
    },

    onSiteVisiteDisabledDates () {
      const toDate = new Date(Date.now())
      toDate.setDate(toDate.getDate() - 1)
      const fromDate = new Date(this.closingDate)
      fromDate.setDate(fromDate.getDate() - 1)
      return {
        from: fromDate,
        to: toDate,
      }
    },
  },
  computed: {
    closingTimeHklOptions () {
      return this.closingTimeOptions.map((value) => ({
        name: value,
        value,
      }))
    },
    visitTimeHklOptions () {
      return this.visitTimes.map((value) => ({
        name: value || '--/--',
        value,
      }))
    },
    ...mapGetters('tenders', ['currentTender', 'isAddendum']),
    onsiteVisitRequired () {
      return this.currentTender.general.onsiteVisitRequired
    },
    disabledDates () {
      return {
        to: this.isAddendum && this.currentTender.general.closingDateExtended ? new Date(this.lastVersionClosingDate) : this.toDate,
      }
    },
    status () {
      return parseStatusText(this.currentTender.general)
    },
    onsiteLocation () {
      return this.currentTender.general.onsiteLocation || ''
    },
    onsiteContact () {
      const defaultContact = {
        name: '',
        email: '',
        phone: '',
      }
      return this.currentTender.general.onsiteContact || defaultContact
    },

    closingDate () {
      if (!this.currentTender.general.closingDate) {
        this.selectedClosingDate = null
        this.selectedClosingTime = null
        return
      }
      const closingDate = this.currentTender.general.closingDate
      const [parsedClosingDate, parsedClosingTime] = dateUtil.parseClosingDateTime(this.currentTender.general.closingDate)
      this.selectedClosingDate = parsedClosingDate
      this.selectedClosingTime = parsedClosingTime

      return closingDate
    },

    bids () {
      if (!this.currentTender.bids) {
        return []
      }
      const visitTimes = {}
      const bids = this.currentTender.bids
      for (let i = 0; i < bids.length; i += 1) {
        const bid = bids[i]
        const visitDate = (bid.visitDate) ? dateUtil.convertDateToArray(bid.visitDate, dateFormat, '-') : ''
        if (visitDate) {
          if (!this.originVisitTimeSet) {
            this.originalVisitTimes[bid.id] = dateUtil.toMoment(bid.visitDate).valueOf()
          }
          visitTimes[i] = {
            visitDay: visitDate[0].trim(),
            visitTime: visitDate[1].trim(),
          }
        } else {
          visitTimes[i] = {
            visitDay: bid.visitDay || '',
            visitTime: bid.visitTime || '',
          }
        }
      }
      this.originVisitTimeSet = true
      this.onsiteVisitTimes = visitTimes
      return bids
    },
    isTenderInAddendum () {
      return isTenderInAddendum(this.currentTender.general)
    },

  },
  async mounted () {
    if (this.isAddendum && this.currentTender.general.closingDateExtended && this.currentTender.general.version) {
      const revision = await getRevisionByVersionNum(this.currentTender.general.id, this.currentTender.general.version - 1)
      this.lastVersionClosingDate = deep(revision, 'closingDate')
    }
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/edit';
@import '~styles/table';

table {
  margin-bottom: 0;
}

.row {
  margin-left: 0;
}
.closing-date-container {
  padding: 20px 24px;
  max-width: 1440px;
  margin: 0 auto;
}
.tender-status-label, .status-severity {
  font-family: Georgia;
  font-size: 20px;
  text-align: center;
  color: #0c3a58;
  margin: 0 0;
}

.status-severity {
  margin: 0 2vw;
}

.closing-date-label {
  margin: 2.5vh 0;
  font-family: Verdana;
  font-size: 14px;
  line-height: 1.43;
  text-align: left;
  color: #0C3A58;
}

.closing-time {
  margin: 0 2vw;
  max-width: 170px;
}

.onsite-visit {
  margin: 20px 0;
}
.onsite-location {
  &__label {
    margin-bottom: 5px;
  }
  width: 60%!important;
  min-width: 600px;
}
.onsite-contact {
  &__label {
    margin-top: 10px;
    margin-bottom: 5px;
  }
  width: 40%!important;
  min-width: 400px;
}

.vendor-name {
  width: 50%;
  min-width: 350px;
  font-family: Verdana;
  font-size: 14px;
  line-height: 1.43;
  text-align: left;
  word-wrap: break-word;
  color: #0C3A58;
}

.clear-date {
  min-width: 16px;
  display: table-cell;
  vertical-align: middle;

  .fa-times {
    cursor: pointer;
  }
}

.visit-date {
  height: 100%;
  display: inline-block;

  .vdp-datepicker {
    max-width: 130px;
    min-width: 130px;
    display: table-cell;
    vertical-align: middle;
  }
}

.visit-time {
  max-width: 100px;
  width: 100px;
  min-width: 100px;
  display: table-cell;
  vertical-align: middle;
  font-family: Verdana;
  font-size: 14px;
  line-height: 1.43;
  text-align: left;
  color: #0C3A58;
  margin: 0 0 0 20px;
  padding: 0 0;
  border: none;
  background-color: transparent;
}
.vendor-status {
  width: 10%;
  min-width: 100px;
  span{
    display: inline-block;
  }
}
.error-highlight {
  background-color: #f9ecea!important;
}
#edit-closing-date-onsite-visit-required {
  vertical-align: middle;
}

.status-summary {
  margin-top: 14px;
  text-align: right;
  label {
    display: inline-block;
    font-weight: bold;
    color: #0C3A58;
  }
  span {
    padding-right: 10px;
  }
}

</style>

<style lang="scss">
.error-highlight .hkl-select .ant-select-selection {
  background-color: #f9ecea;
}

div[id^="edit-closing-date-picker"] .hkl-select .ant-select-selection {
  width: 100%;
  border: none;
}
td[id^="edit-closing-date-vendors"] {
  .vdp-datepicker, .hkl-select .ant-select-selection {
    border: none;
    border-right: none;
    background-color: transparent;
    padding: 0 0;

    input {
      background-color: transparent;
      border: none;
    }
  }
}
.visit-time.hkl-select.ant-select-disabled .ant-select-selection {
  background: transparent;
}

</style>
